import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import './RiderPuzzle.css';

// Helper to truncate to exactly one dot if name is too long for the 52px cell
const truncateToOneDot = (name, maxLen = 9) => {
  if (!name) return '';
  return name.length > maxLen ? name.substring(0, maxLen - 1) + '.' : name;
};

function RiderPuzzle({
  gameMode,
  selectedRiders,
  onToggleRider,
  riders,
  favoriteRiders,
  apiUrl,
  sortBy,
  onSortByChange,
  filterFavorites,
  onFilterFavoritesChange,
  filterSelected,
  onFilterSelectedChange,
  selectedRaces,
  onSelectedRacesChange,
  selectedTeams,
  onSelectedTeamsChange,
  tableView,
  onTableViewChange,
  searchTerm,
  onSearchTermChange,
  maxFavoritesRankPerRace = 1,
  configurations = [],
  activeConfigId,
  onSwitchConfiguration,
  onAddConfiguration,
  onDeleteConfiguration,
  isConfigurationOverBudget,
  plannedSwaps = [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
  selectedSwapAfterRace = { '0': null, '1': null, '2': null },
  appliedSwaps = [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
  appliedSelectedSwapAfterRace = { '0': null, '1': null, '2': null },
  onUpdateSwaps = () => { },
  onUpdateConfigName = () => { },
  competitions = [],
  onModalStateChange = () => { },
  onShowTop20 = () => { },
  onShowHistory = () => { },
  onOpenNotes = () => { },
  races = [],
  renderParts = 'all', // 'selection', 'table', or 'all'
  totalBudget = 45000000,
  notes = {},
  invalidRiderNames = []
}) {
  const [allRiders, setAllRiders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Extract unique teams from riders that are actually in the current startlist
  // Sorted by average rider price (highest to lowest)
  const uniqueTeams = useMemo(() => {
    const ridersInGame = (allRiders || []).filter(r => r.races.length > 0);
    const teamStats = {};
    const teamsSet = new Set();

    ridersInGame.forEach(r => {
      if (r.teamname) {
        teamsSet.add(r.teamname);
        if (!teamStats[r.teamname]) teamStats[r.teamname] = { total: 0, count: 0 };
        teamStats[r.teamname].total += (r.price || 0);
        teamStats[r.teamname].count += 1;
      }
    });

    return Array.from(teamsSet).sort((a, b) => {
      const avgA = teamStats[a].total / teamStats[a].count;
      const avgB = teamStats[b].total / teamStats[b].count;
      return avgB - avgA;
    });
  }, [allRiders]);

  // Helper to check if swaps are valid for applying
  // - No partial swaps (no swap with only out OR only in)
  // - Empty swaps are allowed, complete swaps are allowed
  // - Just no mixing of partial stuff
  const checkSwapsValid = (planned, races) => {
    // No partial swaps allowed (only out or only in)
    const hasPartial = planned.some((swap) => {
      const hasOut = !!swap.out;
      const hasIn = !!swap.in;
      // Partial = one is filled but not the other
      return (hasOut && !hasIn) || (!hasOut && hasIn);
    });

    // Allow if no partial swaps (empty swaps and complete swaps are both allowed)
    return !hasPartial;
  };


  const calculateSwapDemand = (swaps, swapRaces) => {
    if (!swaps || !swapRaces) return 0;
    const validSwaps = [];
    swaps.forEach((swap, index) => {
      if (swap.out && swap.in && swapRaces[index]) {
        const raceIndex = races.indexOf(swapRaces[index]);
        const outRiderInfo = riders[swap.out];
        const outPrice = typeof outRiderInfo === 'object' ? outRiderInfo.price : (outRiderInfo || 500000);
        const inRiderInfo = riders[swap.in];
        const inPrice = typeof inRiderInfo === 'object' ? inRiderInfo.price : (inRiderInfo || 500000);
        validSwaps.push({ index, raceIndex, outPrice, inPrice });
      }
    });

    // Group by raceIndex
    const raceCosts = {};
    validSwaps.forEach(swap => {
      if (!raceCosts[swap.raceIndex]) {
        raceCosts[swap.raceIndex] = 0;
      }
      raceCosts[swap.raceIndex] += (swap.inPrice - swap.outPrice);
    });

    // Sort race indices chronologically
    const sortedRaces = Object.keys(raceCosts).map(Number).sort((a, b) => a - b);

    let swapPot = 0;
    let swapDemand = 0;

    sortedRaces.forEach(raceIdx => {
      const cost = raceCosts[raceIdx];
      if (cost < 0) {
        swapPot += Math.abs(cost);
      } else if (cost > 0) {
        const paidFromPot = Math.min(cost, swapPot);
        swapPot -= paidFromPot;
        const remainingCost = cost - paidFromPot;
        swapDemand += remainingCost;
      }
    });

    return swapDemand;
  };

  // Robust rider name comparison
  const isSame = (a, b) => a?.trim().toLowerCase() === b?.trim().toLowerCase();

  // Calculate favorite counts per rider
  const favCountByRider = useMemo(() => {
    const counts = {};
    const relevantRaces = new Set(races || []);
    Object.entries(favoriteRiders || {}).forEach(([race, list]) => {
      if (!relevantRaces.has(race)) return; // Scope check
      (list || []).forEach(f => {
        if (!f.rider) return;
        const name = f.rider.trim().toLowerCase();
        counts[name] = (counts[name] || 0) + 1;
      });
    });
    return counts;
  }, [favoriteRiders, races]);

  // Memoize max ranks per race for color scaling (minimum 10)
  const maxRanksPerRace = useMemo(() => {
    const result = {};
    Object.entries(favoriteRiders || {}).forEach(([race, list]) => {
      const listMax = (list || []).reduce((max, f) => Math.max(max, f.rank || 0), 0);
      result[race] = Math.max(10, listMax);
    });
    return result;
  }, [favoriteRiders]);

  // Generate color based on rank (Green to Red) - ONLY for Giro
  const getRankColor = (rank, race) => {
    if (gameMode === 'klassiekers') return null;
    const maxRank = maxRanksPerRace[race];
    if (!rank || !maxRank) return null;

    // Position 0 (rank 1) to 1 (rank maxRank)
    // Force re-compile to clear HMR cache
    const pos = (rank - 1) / (maxRank - 1 || 9);

    // Piecewise Hue interpolation to ensure distinct Yellow and Orange steps
    // Fasen: 0-1/3 (Groen->Geel), 1/3-2/3 (Geel->Oranje), 2/3-1.0 (Oranje->Rood)
    const third = 1 / 3;
    const twoThirds = 2 / 3;
    let hue;
    if (pos <= third) {
      // Green (120) to Yellow (60)
      hue = 120 - (pos / third) * 60;
    } else if (pos <= twoThirds) {
      // Yellow (60) to Orange (30)
      hue = 60 - ((pos - third) / third) * 30;
    } else {
      // Orange (30) to Red (0)
      hue = 30 - ((pos - twoThirds) / third) * 30;
    }

    const sat = 100; // Maximale verzadiging voor felle kleuren

    // Lightness afstemmen op de kleur (hue) voor maximale impact en leesbaarheid
    let light;
    if (hue >= 100) {
      light = 38; // Donkerder groen
    } else if (hue >= 50) {
      light = 44; // Geel: iets minder licht gemaakt voor leesbaarheid witte tekst
    } else if (hue >= 25) {
      light = 50; // Echt fel oranje
    } else {
      light = 45; // Krachtig rood
    }

    return `hsl(${hue}, ${sat}%, ${light}%)`;
  };

  const teamFilterRef = useRef(null);
  const [showTeamFilter, setShowTeamFilter] = useState(false);
  const [showRaceFilter, setShowRaceFilter] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const [hoveredHeader, setHoveredHeader] = useState(null);
  const [showHeaderTooltip, setShowHeaderTooltip] = useState(false);
  const [headerTooltipPos, setHeaderTooltipPos] = useState({ top: 0, left: 0 });
  const [showPlannedSwaps, setShowPlannedSwaps] = useState(false);
  const [showInRiderModal, setShowInRiderModal] = useState(false);
  const [selectedInModalIndex, setSelectedInModalIndex] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null); // e.g., "0-out", "0-in", "1-out"
  const [inputValues, setInputValues] = useState({
    '0-out': '', '0-in': '',
    '1-out': '', '1-in': '',
    '2-out': '', '2-in': ''
  });


  const [showSwapWarning, setShowSwapWarning] = useState(false);
  const [swapWarningData, setSwapWarningData] = useState({
    riderName: '',
    affectedSwaps: [],
    indexToRemove: null,
    pendingRemove: null,
    dependencyType: '', // 'in-used-as-out' or 'out-used-as-in'
    warningType: 'removal' // 'removal' or 'in-rider-conflict'
  });
  const [showDeselectWarning, setShowDeselectWarning] = useState(false);
  const [deselectWarningData, setDeselectWarningData] = useState({
    riderName: '',
    swapIndices: [],
    pendingRider: null,
    budgetIssues: []
  });
  const [showRaceSelectionModal, setShowRaceSelectionModal] = useState(false);
  const [selectedRaceModalIndex, setSelectedRaceModalIndex] = useState(null);
  const [showNamingModal, setShowNamingModal] = useState(false);
  const [namingConfigId, setNamingConfigId] = useState(null);
  const [tempConfigName, setTempConfigName] = useState('');
  const [inModalFilterFavs, setInModalFilterFavs] = useState(false);
  const [inModalSortBy, setInModalSortBy] = useState('price');
  const [inModalSelectedRaces, setInModalSelectedRaces] = useState([]);
  const [showInModalRaceFilter, setShowInModalRaceFilter] = useState(false);
  const raceFilterRef = useRef(null);
  const inModalRaceFilterRef = useRef(null);
  const favoritesCheckboxRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  // Notify parent of modal state changes for scroll locking
  useEffect(() => {
    const isAnyInternalModalOpen =
      showPlannedSwaps ||
      showInRiderModal ||
      showRaceSelectionModal ||
      showSwapWarning ||
      showDeselectWarning;

    // Immediate DOM update for zero flicker
    if (isAnyInternalModalOpen) {
      document.documentElement.classList.add('no-scroll');
    }

    onModalStateChange(isAnyInternalModalOpen);
  }, [
    showPlannedSwaps,
    showInRiderModal,
    showRaceSelectionModal,
    showSwapWarning,
    showDeselectWarning,
    onModalStateChange
  ]);

  // Helper to deep copy swap arrays (prevent shared object references)
  const deepCopySwaps = (swaps) => swaps.map(swap => ({ ...swap }));

  // Helper to safely update a single swap in array without mutating originals
  const updateSwapAtIndex = (swaps, index, updates) =>
    swaps.map((swap, idx) => idx === index ? { ...swap, ...updates } : swap);

  // Working state for modal edits (local to this component)
  // Use deep copies to prevent accidental mutation of parent state
  const [workingPlannedSwaps, setWorkingPlannedSwaps] = useState(() => deepCopySwaps(plannedSwaps));
  const [workingSelectedSwapAfterRace, setWorkingSelectedSwapAfterRace] = useState(selectedSwapAfterRace);

  // Sync working state when props change (with deep copy to isolate from parent)
  useEffect(() => {
    setWorkingPlannedSwaps(deepCopySwaps(plannedSwaps));
    setWorkingSelectedSwapAfterRace(selectedSwapAfterRace);
  }, [plannedSwaps, selectedSwapAfterRace]);

  // Helper to update both working state (with deep copy to isolate)
  const updateWorkingSwaps = (newPlanned, newRaces) => {
    setWorkingPlannedSwaps(deepCopySwaps(newPlanned));
    setWorkingSelectedSwapAfterRace(newRaces);
  };

  // Commit changes to parent (for Toepassen, delete from summary, deselect)
  const commitSwaps = (newPlanned, newRaces, newApplied, newAppliedRaces) => {
    setWorkingPlannedSwaps(deepCopySwaps(newPlanned));
    setWorkingSelectedSwapAfterRace(newRaces);
    onUpdateSwaps(deepCopySwaps(newPlanned), newRaces, deepCopySwaps(newApplied), newAppliedRaces);
  };

  // Helper function to get rider price

  // Helper function to get rider price
  const getRiderPrice = (riderName) => {
    const riderData = allRiders.find(r => r.rider === riderName);
    return riderData?.price || null;
  };

  // Helper function to get effective team for a specific race (considering applied swaps)
  const getEffectiveTeamForRace = React.useCallback((raceName) => {
    const raceIndex = races.indexOf(raceName);
    const riderMap = Object.fromEntries(allRiders.map(a => [a.rider, a]));

    // Start with original selected riders
    let effectiveTeam = [...(selectedRiders || [])];

    // Apply APPLIED swaps (not draft swaps)
    appliedSwaps.forEach((swap, swapIdx) => {
      if (swap.out && swap.in && appliedSelectedSwapAfterRace[swapIdx]) {
        const swapRaceIndex = races.indexOf(appliedSelectedSwapAfterRace[swapIdx]);

        // If current race is AFTER the swap race
        if (raceIndex > swapRaceIndex) {
          // Remove the OUT rider
          effectiveTeam = effectiveTeam.filter(r => r !== swap.out);

          // Add the IN rider (if races in this race and not already there)
          const inRiderRaces = riderMap[swap.in]?.races || [];
          if (inRiderRaces.includes(raceName) && !effectiveTeam.includes(swap.in)) {
            effectiveTeam.push(swap.in);
          }
        }
        // If race is AT or BEFORE swap race, keep original team
      }
    });

    return effectiveTeam;
  }, [allRiders, selectedRiders, appliedSwaps, appliedSelectedSwapAfterRace, races]);

  // Calculate total participations across all races
  const totalParticipations = React.useMemo(() => {
    if (gameMode !== 'klassiekers') return 0;
    const riderMap = Object.fromEntries(allRiders.map(a => [a.rider, a]));
    let total = 0;
    races.forEach(race => {
      const effectiveTeam = getEffectiveTeamForRace(race);
      effectiveTeam.forEach(rider => {
        if (riderMap[rider]?.races?.includes(race)) {
          total++;
        }
      });
    });
    return total;
  }, [allRiders, getEffectiveTeamForRace, races, gameMode]);

  // Check if rider is being swapped OUT in this race (last race before leaving)
  const getSwapOutRace = (riderName) => {
    const riderMap = Object.fromEntries(allRiders.map(a => [a.rider, a]));

    for (let i = 0; i < appliedSwaps.length; i++) {
      if (appliedSwaps[i].out === riderName && appliedSelectedSwapAfterRace[i]) {
        const swapRace = appliedSelectedSwapAfterRace[i];
        const riderRaces = riderMap[riderName]?.races || [];

        // Find last race AT OR BEFORE swapRace where rider races
        const swapRaceIndex = races.indexOf(swapRace);
        for (let j = swapRaceIndex; j >= 0; j--) {
          const race = races[j];
          if (riderRaces.includes(race)) {
            return race;
          }
        }
      }
    }
    return null;
  };

  // Check if rider is being swapped IN in this race (first race after entering)
  const getSwapInRace = (riderName) => {
    const riderMap = Object.fromEntries(allRiders.map(a => [a.rider, a]));

    for (let i = 0; i < appliedSwaps.length; i++) {
      if (appliedSwaps[i].in === riderName && appliedSelectedSwapAfterRace[i]) {
        const swapRace = appliedSelectedSwapAfterRace[i];
        const riderRaces = riderMap[riderName]?.races || [];

        // Find first race AFTER swapRace where rider races
        const swapRaceIndex = races.indexOf(swapRace);
        for (let j = swapRaceIndex + 1; j < races.length; j++) {
          const race = races[j];
          if (riderRaces.includes(race)) {
            return race;
          }
        }
      }
    }
    return null;
  };

  // Check if removing a swap will break dependent swaps
  const checkSwapDependencies = (indexToRemove) => {
    const swapToRemove = workingPlannedSwaps[indexToRemove];
    const dependentIndices = [];
    let dependencyType = ''; // 'in-used-as-out' or 'out-used-as-in'
    let riderName = '';

    // Check 1: If this "in" rider is used as "out" in any later swaps
    if (swapToRemove.in) {
      const inRider = swapToRemove.in;
      for (let i = indexToRemove + 1; i < workingPlannedSwaps.length; i++) {
        if (workingPlannedSwaps[i].out === inRider) {
          dependentIndices.push(i);
        }
      }
      if (dependentIndices.length > 0) {
        dependencyType = 'in-used-as-out';
        riderName = inRider;
      }
    }

    // Check 2: If this "out" rider is used as "in" in any later swaps
    if (swapToRemove.out && dependentIndices.length === 0) {
      const outRider = swapToRemove.out;
      for (let i = indexToRemove + 1; i < workingPlannedSwaps.length; i++) {
        if (workingPlannedSwaps[i].in === outRider) {
          dependentIndices.push(i);
        }
      }
      if (dependentIndices.length > 0) {
        dependencyType = 'out-used-as-in';
        riderName = outRider;
      }
    }

    return {
      hasDependents: dependentIndices.length > 0,
      dependentIndices,
      riderName,
      dependencyType
    };
  };

  // Check if deselecting a rider will remove swaps (rider used as OUT)
  const checkDeselectSwaps = (riderName) => {
    const swapIndices = [];
    const allAffected = new Set();

    // Find all swaps where this rider is OUT
    workingPlannedSwaps.forEach((swap, idx) => {
      if (swap.out === riderName) {
        swapIndices.push(idx);
        allAffected.add(idx);
      }
    });

    // For each swap that will be removed, check cascade effects
    swapIndices.forEach(idx => {
      const deps = checkSwapDependencies(idx);
      if (deps.hasDependents) {
        deps.dependentIndices.forEach(depIdx => allAffected.add(depIdx));
      }
    });

    return {
      hasSwaps: swapIndices.length > 0,
      swapIndices: Array.from(allAffected).sort((a, b) => a - b),
      riderName,
      budgetIssues: [] // kept for backwards compat with warning modal
    };
  };

  // Helper to remove a swap with confirmation if needed
  const removeSwapWithValidation = (indexToRemove) => {
    const deps = checkSwapDependencies(indexToRemove);

    if (deps.hasDependents) {
      // Show warning
      setSwapWarningData({
        riderName: deps.riderName,
        affectedSwaps: deps.dependentIndices,
        indexToRemove,
        pendingRemove: { index: indexToRemove, type: 'out' },
        dependencyType: deps.dependencyType
      });
      setShowSwapWarning(true);
      return;
    }

    // No dependents - remove directly (pass empty array for dependent indices)
    performSwapRemoval(indexToRemove, []);
  };

  // Actually remove the swap and compact
  const performSwapRemoval = (indexToRemove, affectedIndices = []) => {
    let newSwaps = [...workingPlannedSwaps];

    // Collect all indices to remove: the main one + all dependent ones
    const indicesToRemove = [indexToRemove, ...affectedIndices];

    // Remove in reverse order so indices don't shift
    indicesToRemove.sort((a, b) => b - a).forEach(idx => {
      if (newSwaps[idx]) {
        newSwaps[idx].out = '';
        newSwaps[idx].in = '';
      }
    });

    // Compact: move lower rows up to fill gaps
    const compactSwaps = [];
    const compactRaces = {};

    newSwaps.forEach((swap, idx) => {
      if (swap.out || swap.in) {
        compactSwaps.push(swap);
        const newIdx = compactSwaps.length - 1;
        if (workingSelectedSwapAfterRace[idx]) {
          compactRaces[newIdx] = workingSelectedSwapAfterRace[idx];
        }
      }
    });

    while (compactSwaps.length < 3) {
      compactSwaps.push({ out: '', in: '' });
    }

    const finalRaces = {
      '0': compactRaces[0] || null,
      '1': compactRaces[1] || null,
      '2': compactRaces[2] || null
    };

    // Update working swaps (local state in modal) - not committed until Toepassen is clicked
    updateWorkingSwaps(compactSwaps, finalRaces);

    const resetInputValues = {
      '0-out': '', '0-in': '',
      '1-out': '', '1-in': '',
      '2-out': '', '2-in': ''
    };
    setInputValues(resetInputValues);
    setOpenDropdown(null);
    setShowSwapWarning(false);
  };

  // Check dependencies for applied swaps (in summary menu)
  const checkAppliedSwapDependencies = (indexToRemove) => {
    const swapToRemove = appliedSwaps[indexToRemove];
    const dependentIndices = [];
    let dependencyType = '';
    let riderName = '';

    // Check 1: If this "in" rider is used as "out" in any later swaps
    if (swapToRemove.in) {
      const inRider = swapToRemove.in;
      for (let i = indexToRemove + 1; i < appliedSwaps.length; i++) {
        if (appliedSwaps[i].out === inRider) {
          dependentIndices.push(i);
        }
      }
      if (dependentIndices.length > 0) {
        dependencyType = 'in-used-as-out';
        riderName = inRider;
      }
    }

    // Check 2: If this "out" rider is used as "in" in any later swaps
    if (swapToRemove.out && dependentIndices.length === 0) {
      const outRider = swapToRemove.out;
      for (let i = indexToRemove + 1; i < appliedSwaps.length; i++) {
        if (appliedSwaps[i].in === outRider) {
          dependentIndices.push(i);
        }
      }
      if (dependentIndices.length > 0) {
        dependencyType = 'out-used-as-in';
        riderName = outRider;
      }
    }

    return {
      hasDependents: dependentIndices.length > 0,
      dependentIndices,
      riderName,
      dependencyType
    };
  };

  // Check if changing this "in" rider will affect a later "out" rider
  const checkInRiderChange = (rowIndex) => {
    const currentInRider = workingPlannedSwaps[rowIndex]?.in;
    if (!currentInRider) return { hasConflict: false, conflictIndex: null };

    // Check if current "in" rider is used as "out" in any later swaps
    for (let i = rowIndex + 1; i < workingPlannedSwaps.length; i++) {
      if (workingPlannedSwaps[i].out === currentInRider) {
        return {
          hasConflict: true,
          conflictIndex: i
        };
      }
    }
    return { hasConflict: false, conflictIndex: null };
  };

  // Remove swap from applied (summary menu) - with dependency warnings but no cascade deletion
  const removeAppliedSwapWithValidation = (indexToRemove) => {
    const deps = checkAppliedSwapDependencies(indexToRemove);

    const allAffected = [...deps.dependentIndices];

    if (deps.hasDependents) {
      // Show warning about dependent swaps
      setSwapWarningData({
        riderName: deps.riderName || 'onbekend',
        affectedSwaps: allAffected,
        indexToRemove,
        pendingRemove: { index: indexToRemove, type: 'applied' },
        dependencyType: deps.dependencyType,
        budgetIssues: []
      });
      setShowSwapWarning(true);
      return;
    }

    // No dependents and no budget issues - remove directly
    performAppliedSwapRemoval(indexToRemove, []);
  };

  // Actually remove from applied swaps and compact
  const performAppliedSwapRemoval = (indexToRemove, affectedIndices = []) => {
    let newSwaps = [...appliedSwaps];

    // Collect all indices to remove: the main one + all dependent ones
    const indicesToRemove = [indexToRemove, ...affectedIndices];

    // Remove in reverse order so indices don't shift
    indicesToRemove.sort((a, b) => b - a).forEach(idx => {
      if (newSwaps[idx]) {
        newSwaps[idx].out = '';
        newSwaps[idx].in = '';
      }
    });

    // Compact: move lower rows up to fill gaps
    const compactSwaps = [];
    const compactRaces = {};

    newSwaps.forEach((swap, idx) => {
      if (swap.out || swap.in) {
        compactSwaps.push(swap);
        const newIdx = compactSwaps.length - 1;
        if (appliedSelectedSwapAfterRace[idx]) {
          compactRaces[newIdx] = appliedSelectedSwapAfterRace[idx];
        }
      }
    });

    while (compactSwaps.length < 3) {
      compactSwaps.push({ out: '', in: '' });
    }

    const finalRaces = {
      '0': compactRaces[0] || null,
      '1': compactRaces[1] || null,
      '2': compactRaces[2] || null
    };

    // Update both applied and planned swaps to keep them in sync via commitSwaps
    commitSwaps(compactSwaps, finalRaces, compactSwaps, finalRaces);

    const resetInputValues = {
      '0-out': '', '0-in': '',
      '1-out': '', '1-in': '',
      '2-out': '', '2-in': ''
    };
    setInputValues(resetInputValues);

    setShowSwapWarning(false);
  };

  // Handle in-rider change: remove conflicting swap and set new in-rider
  const handleInRiderConflictResolution = () => {
    const rowIndex = swapWarningData.pendingRemove.index;
    const newRider = swapWarningData.pendingRemove.newRider;
    const conflictIndex = swapWarningData.affectedSwaps[0];

    // Remove the conflicting swap
    let newSwaps = [...workingPlannedSwaps];
    newSwaps[conflictIndex].out = '';
    newSwaps[conflictIndex].in = '';

    // Compact remaining swaps
    const compactSwaps = [];
    const compactRaces = {};
    newSwaps.forEach((swap, idx) => {
      if (swap.out || swap.in) {
        compactSwaps.push(swap);
        const newIdx = compactSwaps.length - 1;
        if (workingSelectedSwapAfterRace[idx]) {
          compactRaces[newIdx] = workingSelectedSwapAfterRace[idx];
        }
      }
    });

    while (compactSwaps.length < 3) {
      compactSwaps.push({ out: '', in: '' });
    }

    // Find the new index after compaction
    let newRowIndex = rowIndex;
    let count = 0;
    for (let i = 0; i < rowIndex; i++) {
      if (newSwaps[i].out || newSwaps[i].in) count++;
    }
    newRowIndex = count;

    // Now set the new in-rider
    const finalSwaps = [...compactSwaps];
    finalSwaps[newRowIndex].in = newRider;

    const finalRaces = {
      '0': compactRaces[0] || null,
      '1': compactRaces[1] || null,
      '2': compactRaces[2] || null
    };

    // Update working swaps (local state in modal) - not committed until Toepassen is clicked
    updateWorkingSwaps(finalSwaps, finalRaces);

    const resetInputValues = {
      '0-out': '', '0-in': '',
      '1-out': '', '1-in': '',
      '2-out': '', '2-in': ''
    };
    setInputValues(resetInputValues);
    setShowInRiderModal(false);
    setShowSwapWarning(false);
  };

  // Handler for deselecting a rider (with swap validation)
  const handleDeselectRider = (riderName) => {
    const swapCheck = checkDeselectSwaps(riderName);

    if (swapCheck.hasSwaps) {
      // Show warning about swaps that will be removed
      setDeselectWarningData({
        riderName: swapCheck.riderName,
        swapIndices: swapCheck.swapIndices,
        pendingRider: riderName,
        budgetIssues: swapCheck.budgetIssues || []
      });
      setShowDeselectWarning(true);
      return;
    }

    // No swaps to remove, just deselect normally
    onToggleRider(riderName);
  };

  // Perform deselect and remove affected swaps
  const performDeselectWithSwapRemoval = (riderName, swapIndices) => {
    // First remove all affected swaps
    let newSwaps = [...workingPlannedSwaps];
    swapIndices.sort((a, b) => b - a).forEach(idx => {
      if (newSwaps[idx]) {
        newSwaps[idx].out = '';
        newSwaps[idx].in = '';
      }
    });

    // Compact remaining swaps
    const compactSwaps = [];
    const compactRaces = {};
    newSwaps.forEach((swap, idx) => {
      if (swap.out || swap.in) {
        compactSwaps.push(swap);
        const newIdx = compactSwaps.length - 1;
        if (workingSelectedSwapAfterRace[idx]) {
          compactRaces[newIdx] = workingSelectedSwapAfterRace[idx];
        }
      }
    });

    while (compactSwaps.length < 3) {
      compactSwaps.push({ out: '', in: '' });
    }

    const finalRaces = {
      '0': compactRaces[0] || null,
      '1': compactRaces[1] || null,
      '2': compactRaces[2] || null
    };

    // Update both planned and applied swaps via commitSwaps
    commitSwaps(compactSwaps, finalRaces, compactSwaps, finalRaces);

    const resetInputValues = {
      '0-out': '', '0-in': '',
      '1-out': '', '1-in': '',
      '2-out': '', '2-in': ''
    };
    setInputValues(resetInputValues);

    // Now deselect the rider
    onToggleRider(riderName);
    setShowDeselectWarning(false);
  };

  // Helper to check if inRider is valid for this rowIndex and race
  const isInRiderValid = (rowIndex, inRider, race) => {
    if (!inRider) return true; // Empty is ok

    const outRider = workingPlannedSwaps[rowIndex]?.out;
    if (inRider === outRider) return false; // Can't be same as out rider

    const usedInRiders = workingPlannedSwaps
      .map((swap, idx) => idx !== rowIndex ? swap.in : null)
      .filter(r => r);
    if (usedInRiders.includes(inRider)) return false; // Already used in other "in" field

    // If inRider comes from an earlier "out", check race constraint
    for (let i = 0; i < rowIndex; i++) {
      if (workingPlannedSwaps[i].out === inRider && workingSelectedSwapAfterRace[i]) {
        const outRaceIndex = races.indexOf(workingSelectedSwapAfterRace[i]);
        const currentRaceIndex = races.indexOf(race);
        if (currentRaceIndex <= outRaceIndex) {
          return false; // Current race is not later than out race
        }
      }
    }

    return true;
  };


  // Helper function to get available riders for In selector modal with filters and sorting
  const getAvailableRidersForInModal = (index) => {
    // Start with ALL riders
    const selectedSet = new Set(selectedRiders || []);
    const currentRace = workingSelectedSwapAfterRace[index];
    const currentRaceIndex = currentRace ? races.indexOf(currentRace) : -1;

    // For each rider, check if they're available for IN swap
    let filtered = allRiders.map(r => r.rider).filter(rider => {
      // Never swap in the OUT rider of this swap
      if (workingPlannedSwaps[index].out === rider) return false;

      // Track the last swap event for this rider BEFORE this index
      let lastEventType = null; // 'in' or 'out'
      let lastEventRaceIndex = -1;

      const isOriginallySelected = selectedSet.has(rider);

      for (let i = 0; i < index; i++) {
        if (workingPlannedSwaps[i].out === rider && workingSelectedSwapAfterRace[i]) {
          const raceIdx = races.indexOf(workingSelectedSwapAfterRace[i]);
          if (raceIdx > lastEventRaceIndex) {
            lastEventRaceIndex = raceIdx;
            lastEventType = 'out';
          }
        }
        if (workingPlannedSwaps[i].in === rider && workingSelectedSwapAfterRace[i]) {
          const raceIdx = races.indexOf(workingSelectedSwapAfterRace[i]);
          if (raceIdx > lastEventRaceIndex) {
            lastEventRaceIndex = raceIdx;
            lastEventType = 'in';
          }
        }
      }

      // A rider is available for IN if they're NOT currently in the team:
      // - Originally selected + no events = in team (NOT available)
      // - Originally selected + last event OUT = out of team (available, if race is after OUT)
      // - Originally selected + last event IN = in team (NOT available)
      // - Not selected + no events = not in team (available)
      // - Not selected + last event IN = in team (NOT available)
      // - Not selected + last event OUT = out of team (available, if race is after OUT)

      if (isOriginallySelected) {
        if (lastEventType === null) {
          // In team, not available
          return false;
        } else if (lastEventType === 'out') {
          // Was swapped out, can be swapped back in
          // But only if current race is AFTER the out race
          if (currentRaceIndex === -1) return true; // No race selected yet, show as available
          return currentRaceIndex > lastEventRaceIndex;
        } else {
          // Last event is IN, so back in team
          return false;
        }
      } else {
        // Not originally selected
        if (lastEventType === null) {
          // Never involved in swaps, available
          return true;
        } else if (lastEventType === 'in') {
          // Was swapped in, now in team, not available
          return false;
        } else {
          // Last event is OUT, out of team, available again
          // But only if current race is AFTER the out race
          if (currentRaceIndex === -1) return true;
          return currentRaceIndex > lastEventRaceIndex;
        }
      }
    });

    // Filter by favorites if enabled
    if (inModalFilterFavs) {
      const favSet = new Set();
      Object.entries(favoriteRiders || {}).forEach(([race, list]) => {
        (list || []).forEach(f => favSet.add(f.rider));
      });
      filtered = filtered.filter(r => favSet.has(r));
    }

    // Filter by selected races in modal
    if (inModalSelectedRaces.length > 0) {
      filtered = filtered.filter(rider => {
        const riderRaces = allRiders.find(r => r.rider === rider)?.races || [];
        return inModalSelectedRaces.every(race => riderRaces.includes(race));
      });
    }

    // Sort riders
    if (inModalSortBy === 'price') {
      filtered.sort((a, b) => {
        const priceA = getRiderPrice(a) || 0;
        const priceB = getRiderPrice(b) || 0;
        return priceB - priceA; // Descending (duur -> goedkoop)
      });
    } else if (inModalSortBy === 'race_count') {
      filtered.sort((a, b) => {
        const countA = gameMode === 'klassiekers'
          ? (allRiders.find(r => r.rider === a)?.races.length || 0)
          : (favCountByRider[a] || 0);
        const countB = gameMode === 'klassiekers'
          ? (allRiders.find(r => r.rider === b)?.races.length || 0)
          : (favCountByRider[b] || 0);

        if (countB !== countA) return countB - countA;

        // Secondary tie-breaker: Price
        const priceA = getRiderPrice(a) || 0;
        const priceB = getRiderPrice(b) || 0;
        if (priceB !== priceA) return priceB - priceA;

        return a.localeCompare(b);
      });
    } else {
      filtered.sort((a, b) => a.localeCompare(b));
    }

    return filtered;
  };

  // Helper function to get available riders for dropdown
  const getAvailableRiders = (index, type, searchTerm = '') => {
    // Get all selected riders in current configuration
    const availableRiders = selectedRiders || [];

    // For "in" field, filter by price and exclude "out" rider
    if (type === 'in') {
      const outRider = workingPlannedSwaps[index].out;
      const outRiderPrice = getRiderPrice(outRider);
      const selectedRace = workingSelectedSwapAfterRace[index];

      // Get riders already selected in "in" fields
      const usedInRiders = workingPlannedSwaps
        .map((swap, idx) => idx !== index ? swap.in : null)
        .filter(r => r);

      let filtered = availableRiders.filter(rider => {
        if (rider === outRider) return false;
        // Exclude riders already used in other "in" fields
        if (usedInRiders.includes(rider)) return false;
        // Only show riders that are not more expensive than the out rider
        const riderPrice = getRiderPrice(rider);
        if (riderPrice > outRiderPrice) return false;

        // If a race is selected, only show riders who race in that race
        if (selectedRace) {
          const riderRaces = allRiders.find(r => r.rider === rider)?.races || [];
          if (!riderRaces.includes(selectedRace)) return false;
        }

        // Filter by search term (case insensitive)
        if (searchTerm.trim()) {
          const lowerSearch = searchTerm.toLowerCase();
          const rInfo = allRiders.find(r => r.rider === rider);
          return rider.toLowerCase().includes(lowerSearch) ||
            (rInfo?.teamname && rInfo.teamname.toLowerCase().includes(lowerSearch)) ||
            (rInfo?.team_abbr && rInfo.team_abbr.toLowerCase().includes(lowerSearch));
        }

        return true;
      });

      // Sort by price descending (duur naar goedkoop)
      return filtered.sort((a, b) => {
        const priceA = getRiderPrice(a) || 0;
        const priceB = getRiderPrice(b) || 0;
        return priceB - priceA;
      });
    }

    // For "out" field:
    // Basis: alle geselecteerde renners
    // PLUS: renners die eerder IN zijn gewisseld (kunnen weer OUT)
    // MINUS: renners die al bij een andere wissel OUT zijn, TENZIJ ze daarna weer IN zijn

    let outRiderPool = [...availableRiders];

    // Add riders that were selected IN in earlier swaps (can be swapped OUT again)
    for (let i = 0; i < index; i++) {
      if (workingPlannedSwaps[i].in && !outRiderPool.includes(workingPlannedSwaps[i].in)) {
        outRiderPool.push(workingPlannedSwaps[i].in);
      }
    }

    // For each potential OUT rider, check if they're "currently in the team"
    // A rider is in the team if:
    // 1. They are selected AND their last swap event (before this index) is not OUT
    // 2. OR they were swapped IN and not swapped OUT after that (before this index)
    const filtered = outRiderPool.filter(rider => {
      // Find all swap events for this rider BEFORE this index
      let lastEventType = null; // 'in' or 'out'
      let lastEventRaceIndex = -1;

      // Check if rider is originally selected
      const isOriginallySelected = availableRiders.includes(rider);

      for (let i = 0; i < index; i++) {
        if (workingPlannedSwaps[i].out === rider && workingSelectedSwapAfterRace[i]) {
          const raceIdx = races.indexOf(workingSelectedSwapAfterRace[i]);
          if (raceIdx > lastEventRaceIndex) {
            lastEventRaceIndex = raceIdx;
            lastEventType = 'out';
          }
        }
        if (workingPlannedSwaps[i].in === rider && workingSelectedSwapAfterRace[i]) {
          const raceIdx = races.indexOf(workingSelectedSwapAfterRace[i]);
          if (raceIdx > lastEventRaceIndex) {
            lastEventRaceIndex = raceIdx;
            lastEventType = 'in';
          }
        }
      }

      // If originally selected and never swapped: available
      // If originally selected and last event is OUT: not available
      // If originally selected and last event is IN: available (back in team)
      // If not originally selected and swapped IN: available
      // If not originally selected and last event is OUT: not available

      if (isOriginallySelected) {
        if (lastEventType === 'out') return false;
        return true;
      } else {
        // Not originally selected, must have been swapped IN
        if (lastEventType === 'in') return true;
        return false;
      }
    });

    // Sort by price descending (duur naar goedkoop)
    return filtered.sort((a, b) => {
      const priceA = getRiderPrice(a) || 0;
      const priceB = getRiderPrice(b) || 0;
      return priceB - priceA;
    });
  };

  useEffect(() => {
    if (riders && Object.keys(riders).length > 0) {
      // Use props if available (avoids re-fetching and includes custom riders)
      // Note: API riders might not have 'rider' property in values, so we use key
      const arr = Object.entries(riders).map(([name, info]) => ({
        rider: info.rider || name,
        price: info.price || 500000,
        races: info.races || [],
        riderId: info.RiderId || null,
        teamname: info.teamname,
        team_abbr: info.team_abbr,
        team_color: info.team_color,
        // Include all qualities and metadata from riders_info.csv
        klassement: info.klassement || 0,
        klimmen: info.klimmen || 0,
        tijdrijden: info.tijdrijden || 0,
        sprinten: info.sprinten || 0,
        punchen: info.punchen || 0,
        heuvels: info.heuvels || 0,
        kasseien: info.kasseien || 0,
        nationaliteit: info.nationaliteit || '',
        type: info.type || '',
        birth: info.birth || ''
      }));
      setAllRiders(arr);
      setLoading(false);
    } else {
      // Fallback: fetch from API
      const loadRiders = async () => {
        try {
          const res = await axios.get(`${apiUrl}/all-riders`);
          // res.data is expected to be { rider: {price, races}, ... }
          const arr = Object.entries(res.data).map(([rider, info]) => ({
            rider,
            price: info.price || 500000,
            races: info.races || [],
            riderId: info.RiderId || null,
            teamname: info.teamname,
            team_abbr: info.team_abbr,
            team_color: info.team_color,
            // Include all qualities and metadata from riders_info.csv
            klassement: info.klassement || 0,
            klimmen: info.klimmen || 0,
            tijdrijden: info.tijdrijden || 0,
            sprinten: info.sprinten || 0,
            punchen: info.punchen || 0,
            heuvels: info.heuvels || 0,
            kasseien: info.kasseien || 0,
            nationaliteit: info.nationaliteit || '',
            type: info.type || '',
            birth: info.birth || ''
          }));
          setAllRiders(arr);
          setLoading(false);
        } catch (err) {
          console.error('Fout bij laden renners:', err);
          setLoading(false);
        }
      };
      loadRiders();
    }
  }, [apiUrl, riders]);

  // Close race filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if click is on favorites checkbox
      if (favoritesCheckboxRef.current && favoritesCheckboxRef.current.contains(event.target)) {
        return;
      }
      // Close if click is outside the race filter container
      if (raceFilterRef.current && !raceFilterRef.current.contains(event.target)) {
        setShowRaceFilter(false);
      }
    };

    if (showRaceFilter) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showRaceFilter]);

  // Close team filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (teamFilterRef.current && !teamFilterRef.current.contains(event.target)) {
        setShowTeamFilter(false);
      }
    };

    if (showTeamFilter) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showTeamFilter]);

  // Close swap dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on any swap container
      const swapsContainer = event.target.closest('.swaps-modal-content');
      const isClickOnInput = event.target.closest('.swap-input');
      const isClickOnDropdown = event.target.closest('.swap-dropdown');

      if (swapsContainer && !isClickOnInput && !isClickOnDropdown) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdown]);

  // Close race filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inModalRaceFilterRef.current && !inModalRaceFilterRef.current.contains(event.target)) {
        setShowInModalRaceFilter(false);
      }
    };

    if (showInModalRaceFilter) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showInModalRaceFilter]);

  if (loading) {
    return <div className="rider-puzzle loading">Laden...</div>;
  }

  // Calculate coverage table rows: maximum riders in any single race across all configurations
  const calculateMaxCoverageRows = () => {
    let maxRiders = 3; // Minimum 3 rows

    // Check each configuration
    configurations.forEach(config => {
      // For this config, count riders per race
      races.forEach(race => {
        const ridersInRace = config.selectedRiders.filter(
          sr => allRiders.find(r => r.rider === sr)?.races.includes(race)
        ).length;
        maxRiders = Math.max(maxRiders, ridersInRace);
      });
    });

    return maxRiders;
  };

  const maxCoverageRows = calculateMaxCoverageRows();

  // Filter riders based on search and settings
  let filteredRiders = (allRiders || []).slice();

  // Only show riders with at least 1 race
  filteredRiders = filteredRiders.filter(r => r.races.length > 0);

  // Team Filter (OR logic: show if in ANY selected team)
  if (selectedTeams && selectedTeams.length > 0) {
    filteredRiders = filteredRiders.filter(r => selectedTeams.includes(r.teamname));
  }

  // Filter out riders that are swapped IN (from applied swaps)
  // EXCEPT if they are also used as OUT in an earlier swap (circular swap)
  const inRiders = new Set(appliedSwaps.map(swap => swap.in).filter(in_ => in_));
  const outRiders = new Set(appliedSwaps.map(swap => swap.out).filter(out_ => out_));

  // Helper to check if a rider's "out" event comes before their "in" event in race order
  const isValidCircularSwap = (rider) => {
    // Find all swap indices where this rider is "in" or "out"
    const outSwaps = appliedSwaps
      .map((swap, idx) => swap.out === rider ? idx : null)
      .filter(idx => idx !== null);

    const inSwaps = appliedSwaps
      .map((swap, idx) => swap.in === rider ? idx : null)
      .filter(idx => idx !== null);

    if (outSwaps.length === 0 || inSwaps.length === 0) return false;

    // Get race indices for each swap
    const outRaceIndices = outSwaps.map(idx => {
      const race = appliedSelectedSwapAfterRace[idx];
      return race ? races.indexOf(race) : -1;
    });

    const inRaceIndices = inSwaps.map(idx => {
      const race = appliedSelectedSwapAfterRace[idx];
      return race ? races.indexOf(race) : -1;
    });

    // Check if ANY "out" comes before ANY "in"
    const hasEarlyOut = outRaceIndices.some(outIdx =>
      inRaceIndices.some(inIdx => inIdx > outIdx && outIdx >= 0 && inIdx >= 0)
    );

    return hasEarlyOut;
  };

  filteredRiders = filteredRiders.filter(r => {
    // If rider is not in "in", keep them
    if (!inRiders.has(r.rider)) return true;
    // If rider is in "in" but also in "out" with valid circular order, keep them
    if (outRiders.has(r.rider) && isValidCircularSwap(r.rider)) return true;
    // Otherwise exclude them
    return false;
  });

  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    filteredRiders = filteredRiders.filter(r =>
      r.rider.toLowerCase().includes(lowerSearch) ||
      (r.teamname && r.teamname.toLowerCase().includes(lowerSearch)) ||
      (r.team_abbr && r.team_abbr.toLowerCase().includes(lowerSearch))
    );
  }

  if (filterFavorites) {
    const favSet = new Set();
    Object.entries(favoriteRiders || {}).forEach(([race, list]) => {
      (list || []).forEach(f => favSet.add(f.rider));
    });
    filteredRiders = filteredRiders.filter(r => favSet.has(r.rider));
  }

  if (filterSelected) {
    const selectedSet = new Set(selectedRiders);
    filteredRiders = filteredRiders.filter(r => selectedSet.has(r.rider));
  }

  // Filter by selected races
  if (selectedRaces.length > 0) {
    filteredRiders = filteredRiders.filter(r => {
      const rName = (r.rider || '').trim().toLowerCase();

      return selectedRaces.every(race => {
        if (gameMode !== 'klassiekers') {
          // Giro: Must be a favorite in ALL selected etappes
          const favsForRace = favoriteRiders[race] || [];
          return favsForRace.some(f => f.rider && f.rider.trim().toLowerCase() === rName);
        } else {
          // Klassiekers: Must participate in ALL selected races
          return (r.races || []).includes(race);
        }
      });
    });
  }

  // Filter by quality if sorting by quality (hide 0 scores)
  if (['klassement', 'klimmen', 'tijdrijden', 'sprinten', 'heuvels', 'punchen', 'kasseien'].includes(sortBy)) {
    filteredRiders = filteredRiders.filter(r => (r[sortBy] || 0) > 0);
  }

  // Filter for youth in Step 2 if youth sorting is selected
  if (gameMode !== 'klassiekers' && sortBy === 'youth') {
    filteredRiders = filteredRiders.filter(r => r.birth && r.birth >= '2001-01-01');
  }

  // Sort riders
  if (sortBy === 'price') {
    filteredRiders.sort((a, b) => b.price - a.price);
  } else if (sortBy === 'race_count') {
    filteredRiders.sort((a, b) => {
      const nameA = (a.rider || '').trim().toLowerCase();
      const nameB = (b.rider || '').trim().toLowerCase();
      const countA = gameMode === 'klassiekers' ? (a.races || []).length : (favCountByRider[nameA] || 0);
      const countB = gameMode === 'klassiekers' ? (b.races || []).length : (favCountByRider[nameB] || 0);

      // Tier 1: Favorites / Race Count (Descending)
      if (countB !== countA) return countB - countA;

      // Tier 2: Price (Descending)
      if (b.price !== a.price) return b.price - a.price;

      // Tier 3: Name (Ascending)
      return a.rider.localeCompare(b.rider);
    });
  } else if (sortBy === 'name') {
    filteredRiders.sort((a, b) => a.rider.localeCompare(b.rider));
  } else if (sortBy === 'youth') {
    filteredRiders.sort((a, b) => {
      // Tier 1: Klassement quality
      if ((b.klassement || 0) !== (a.klassement || 0)) return (b.klassement || 0) - (a.klassement || 0);
      // Tier 2: Klimmen quality
      if ((b.klimmen || 0) !== (a.klimmen || 0)) return (b.klimmen || 0) - (a.klimmen || 0);
      // Tier 3: Heuvels quality
      if ((b.heuvels || 0) !== (a.heuvels || 0)) return (b.heuvels || 0) - (a.heuvels || 0);
      // Tier 4: Price
      if (b.price !== a.price) return b.price - a.price;
      // Tier 5: Age (Youngest first)
      const valA = a.birth || '1900-01-01';
      const valB = b.birth || '1900-01-01';
      return valB.localeCompare(valA);
    });
  } else {
    // Quality sorting (klassement, klimmen, etc.)
    filteredRiders.sort((a, b) => {
      const valA = a[sortBy] || 0;
      const valB = b[sortBy] || 0;
      if (valB !== valA) return valB - valA;
      // Secondary: price
      return b.price - a.price;
    });
  }

  // Calculate budget
  const spentBudget = selectedRiders.reduce((sum, rider) => {
    const riderData = allRiders.find(r => r.rider === rider);
    return sum + (riderData?.price || 500000);
  }, 0);


  const appliedSwapDemand = calculateSwapDemand(appliedSwaps, appliedSelectedSwapAfterRace);

  // Check if budget will be exceeded
  // If we have > 20 riders: check total of all selected riders
  // If we have <= 20 riders: check total spent + cost to fill remaining slots with minimum price
  let minPossibleTotal;
  if (selectedRiders.length > 20) {
    minPossibleTotal = spentBudget + appliedSwapDemand; // Use all selected riders
  } else {
    minPossibleTotal = spentBudget + Math.max(0, 20 - selectedRiders.length) * 500000 + appliedSwapDemand; // Add cost of remaining slots
  }

  const isOverBudget = minPossibleTotal > totalBudget;

  // Check if rider is a favorite in any of the currently scoped races
  const isFavorite = (riderName) => {
    if (!riderName) return false;
    const nameNorm = riderName.trim().toLowerCase();
    return (favCountByRider[nameNorm] || 0) > 0;
  };

  // Extract last name from full name (first word in "Lastname Firstname" format)
  const getLastName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ').filter(p => p.length > 0);
    return parts.length > 0 ? parts[0] : '';
  };

  // Get favorite races for a rider (scoped to current game mode)
  const getFavoriteRaces = (riderName) => {
    if (!riderName) return [];
    const relevantRaces = new Set(races || []);

    return Object.entries(favoriteRiders || {})
      .filter(([race, _]) => relevantRaces.has(race))
      .filter(([_, list]) => (list || []).some(f => isSame(f.rider, riderName)))
      .map(([race, list]) => {
        const entry = (list || []).find(f => isSame(f.rider, riderName));
        return {
          race,
          rank: entry.rank
        };
      });
  };

  const RACE_ABBR = {
    'Omloop het Nieuwsblad': 'OHN',
    'Kuurne-Brussel-Kuurne': 'KBK',
    'Strade Bianche': 'SB',
    'Paris-Nice': 'PN',
    'Tirreno-Adriatico': 'TA',
    'Milano-Sanremo': 'MSR',
    'Ronde van Brugge': 'RvB',
    'E3 Saxo Classic': 'E3',
    'In Flanders Fields': 'IFF',
    'Dwars door Vlaanderen': 'DDV',
    'Ronde van Vlaanderen': 'RVV',
    'Scheldeprijs': 'SP',
    'Paris-Roubaix': 'PR',
    'Brabantse Pijl': 'BP',
    'Amstel Gold Race': 'AGR',
    'Waalse Pijl': 'WP',
    'Luik-Bastenaken-Luik': 'LBL'
  };

  const getAbbr = (race) => {
    if (RACE_ABBR[race]) return RACE_ABBR[race];
    if (race === 'Eindklassement') return 'EK';
    if (race && race.startsWith('Etappe ')) return race.replace('Etappe ', '');
    return race;
  };

  return (
    <div className={`rider-puzzle ${gameMode !== 'klassiekers' ? 'giro-ui' : ''} ${renderParts === 'table' ? 'giro-full-width-breakout' : ''}`}>
      {(renderParts === 'all' || renderParts === 'selection') && (
        <>
          <div className="puzzle-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder="Zoek renner of ploeg..."
                value={searchTerm}
                onChange={(e) => onSearchTermChange(e.target.value)}
                className="search-input"
                maxLength={100}
              />
              {searchTerm && (
                <button className="clear-button" onClick={() => onSearchTermChange('')}>
                  ✕
                </button>
              )}
            </div>

            <div className="filter-controls">
              <div className="filter-group-checkboxes">
                <label ref={favoritesCheckboxRef}>
                  <input
                    type="checkbox"
                    checked={filterFavorites}
                    onChange={(e) => onFilterFavoritesChange(e.target.checked)}
                  />
                  Alleen favorieten
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={filterSelected}
                    onChange={(e) => onFilterSelectedChange(e.target.checked)}
                  />
                  Alleen geselecteerde
                </label>
              </div>

              <div className="filter-group-selectors">
                <select value={sortBy} onChange={(e) => onSortByChange(e.target.value)}>
                  <option value="price">Sorteer op prijs</option>
                  <option value="race_count">
                    {gameMode === 'klassiekers' ? 'Sorteer op aantal wedstrijden' : 'Sorteer op aantal favoriet'}
                  </option>
                  <option value="name">Sorteer op naam</option>
                  {gameMode !== 'klassiekers' && (
                    <>
                      <option value="klassement">Sorteer op Klassement</option>
                      <option value="klimmen">Sorteer op Klimmen</option>
                      <option value="tijdrijden">Sorteer op Tijdrijden</option>
                      <option value="sprinten">Sorteer op Sprinten</option>
                      <option value="heuvels">Sorteer op Heuvels</option>
                      <option value="punchen">Sorteer op Punchen</option>
                      <option value="kasseien">Sorteer op Kasseien</option>
                      <option value="youth">Sorteer op Jongeren</option>
                    </>
                  )}
                </select>

                <div className="filter-buttons-wrapper">
                  <div className="race-filter-container" ref={raceFilterRef}>
                    <button
                      className="race-filter-button"
                      onClick={() => setShowRaceFilter(!showRaceFilter)}
                    >
                      {gameMode !== 'klassiekers' ? 'Etappe' : 'Wedstrijd'} filter {selectedRaces.length > 0 && `(${selectedRaces.length})`}
                    </button>
                    {showRaceFilter && (
                      <div className="race-filter-dropdown">
                        <div className="race-filter-header">
                          <span>Kies {gameMode !== 'klassiekers' ? 'etappes' : 'wedstrijden'}:</span>
                          {selectedRaces.length > 0 && (
                            <button
                              className="clear-races-button"
                              onClick={() => onSelectedRacesChange([])}
                            >
                              Wissen
                            </button>
                          )}
                        </div>
                        <div className="race-filter-list">
                          {races.map(race => (
                            <label key={race} className="race-filter-item">
                              <input
                                type="checkbox"
                                checked={selectedRaces.includes(race)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    onSelectedRacesChange([...selectedRaces, race]);
                                  } else {
                                    onSelectedRacesChange(selectedRaces.filter(r => r !== race));
                                  }
                                }}
                              />
                              <span>{race}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="team-filter-container" ref={teamFilterRef}>
                    <button
                      className="team-filter-button"
                      onClick={() => setShowTeamFilter(!showTeamFilter)}
                    >
                      Ploeg filter {selectedTeams.length > 0 && `(${selectedTeams.length})`}
                    </button>
                    {showTeamFilter && (
                      <div className="team-filter-dropdown">
                        <div className="team-filter-header">
                          <span>Kies ploegen:</span>
                          {selectedTeams.length > 0 && (
                            <button
                              className="clear-teams-button"
                              onClick={() => onSelectedTeamsChange([])}
                            >
                              Wissen
                            </button>
                          )}
                        </div>
                        <div className="team-filter-list">
                          {uniqueTeams.map(team => (
                            <label key={team} className="team-filter-item">
                              <input
                                type="checkbox"
                                checked={selectedTeams.includes(team)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    onSelectedTeamsChange([...selectedTeams, team]);
                                  } else {
                                    onSelectedTeamsChange(selectedTeams.filter(t => t !== team));
                                  }
                                }}
                              />
                              <span>{team}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>

          {filteredRiders.length === 0 && (
            <div className="no-results">
              Geen renners gevonden
            </div>
          )}

          <div className="riders-puzzle-grid">
            {filteredRiders.map(r => {
              const rider = r.rider;
              const isSelected = selectedRiders.includes(rider);
              const isFav = isFavorite(rider);
              const favoriteRaces = getFavoriteRaces(rider);
              const price = r.price || 0;

              return (
                <div
                  key={rider}
                  className={`puzzle-rider ${isSelected ? 'selected' : ''} ${isFav ? 'favorite' : ''} ${(invalidRiderNames.includes(rider) || r.isInvalid) ? 'invalid' : ''}`}
                  title={(invalidRiderNames.includes(rider) || r.isInvalid) ? "Deze renner staat niet meer op de startlijst!" : ""}
                  onClick={() => {
                    if (isSelected) {
                      // Deselecting - check for swaps
                      handleDeselectRider(rider);
                    } else {
                      // Selecting - no warning needed
                      onToggleRider(rider);
                    }
                  }}
                >
                  <div className="rider-info">
                    <div className="rider-name">{rider}</div>
                    <div className={`rider-price ${gameMode !== 'klassiekers' && price === 10000 ? 'price-red' : ''}`}>
                      € {(price / 1000000).toFixed(2)}M
                      {['klassement', 'klimmen', 'tijdrijden', 'sprinten', 'heuvels', 'punchen', 'kasseien', 'youth'].includes(sortBy) && (
                        <span className={`rider-quality-score ${sortBy !== 'youth' && gameMode !== 'klassiekers' && r[sortBy] === 0.1 ? 'quality-nb' : ''}`}>
                          {sortBy === 'youth' 
                            ? (r.birth ? `${2026 - parseInt(r.birth.split('-')[0])}j` : '??')
                            : (gameMode !== 'klassiekers' && r[sortBy] === 0.1 ? 'NB' : `${(r[sortBy] / 2).toFixed(1).replace('.0', '')} ★`)
                          }
                        </span>
                      )}
                    </div>
                    <div className="favorite-races">
                      {races
                        .filter(rv => r.races.includes(rv))
                        .map(rv => {
                          const fav = favoriteRaces.find(fr => fr.race === rv);

                          // For Grand Tours (like giro), hide non-favorited badges. 
                          // For Klassiekers, keep the grey 'not-fav' badges as originally requested.
                          if (gameMode !== 'klassiekers' && !fav) return null;

                          const abbr = getAbbr(rv);
                          // Always show rank color on cards (if favored and Giro)
                          const color = getRankColor(fav?.rank, rv);

                          return (
                            <span
                              key={rv}
                              className={`race-badge ${fav ? 'fav' : 'not-fav'}`}
                              style={color ? { backgroundColor: color, color: 'white', borderColor: color } : {}}
                            >
                              {abbr}
                            </span>
                          );
                        })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Table toggle section */}
      {(renderParts === 'all' || renderParts === 'table') && (
        <div className={`coverage-section ${gameMode !== 'klassiekers' && renderParts === 'table' ? 'giro-table-wrapper' : ''}`}>
          <div className={`table-header ${gameMode !== 'klassiekers' ? 'giro-table-header-outside' : ''}`}>
            <div className="table-controls">
              {gameMode === 'klassiekers' && (
                <div className="table-toggle">
                  <button
                    className={`toggle-btn ${tableView === 'coverage' ? 'active' : ''}`}
                    onClick={() => onTableViewChange('coverage')}
                  >
                    Dekking
                  </button>
                  <button
                    className={`toggle-btn ${tableView === 'favorites' ? 'active' : ''}`}
                    onClick={() => onTableViewChange('favorites')}
                  >
                    Favorieten
                  </button>
                </div>
              )}
              {gameMode === 'klassiekers' && (
                <button
                  className="planned-swaps-button"
                  onClick={() => setShowPlannedSwaps(!showPlannedSwaps)}
                >
                  Wissels
                </button>
              )}
            </div>
            <div className="config-buttons-group">
              {configurations.map(config => (
                <div key={config.id} className="config-btn-wrapper-small">
                  <button
                    className={`config-btn-small ${config.id === activeConfigId ? 'active' : ''} ${isConfigurationOverBudget(config) ? 'over-budget' : ''}`}
                    onClick={() => onSwitchConfiguration(config.id)}
                  >
                    {config.id}
                    {config.name && (
                      <div className="config-name-hover-overlay">
                        {config.name}
                      </div>
                    )}
                  </button>
                  <button
                    className="config-edit-btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNamingConfigId(config.id);
                      setTempConfigName(config.name || '');
                      setShowNamingModal(true);
                    }}
                    title="Geef deze configuratie een naam"
                  >
                    ✎
                  </button>
                  {configurations.length > 1 && (
                    <button
                      className="config-delete-btn-small"
                      onClick={() => onDeleteConfiguration(config.id)}
                      title="Verwijder deze configuratie"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button className="config-btn-small add-btn" onClick={onAddConfiguration} title="Nieuwe configuratie">
                +
              </button>
            </div>
            <div className={`budget-display ${isOverBudget || selectedRiders.length > 20 ? 'over-budget' : ''}`}>
              <div className="budget-riders-count">
                <span className={`rider-count ${selectedRiders.length > 20 ? 'over' : ''}`}>{selectedRiders.length}</span>
                <span className="budget-separator">/</span>
                <span className="budget-total">20</span>
              </div>
              <div className="budget-counter-separator"></div>
              <div className="budget-amount">
                <span className={`budget-spent ${isOverBudget ? 'over' : ''}`}>€ {((spentBudget + appliedSwapDemand) / 1000000).toFixed(2)}M</span>
                <span className="budget-separator">/</span>
                <span className="budget-total">€ {(totalBudget / 1000000).toFixed(2)}M</span>
              </div>
            </div>
          </div>

          {gameMode === 'klassiekers' && tableView === 'coverage' && (
            <>
              <div className="coverage-table">
                <table>
                  <thead>
                    <tr>
                      <th className="cov-multiplier-header"></th>
                      {races.map(race => {
                        const comp = competitions.find(c => c.TranslatedName === race);
                        return (
                          <th
                            key={race}
                            className={`cov-race-header clickable-header`}
                            onClick={gameMode === 'klassiekers' ? () => onShowTop20(race, comp?.CompetitionId) : () => onOpenNotes(race)}
                            title={gameMode === 'klassiekers' ? "Bekijk top 20 uitslagen" : undefined}
                            onMouseEnter={(e) => {
                              if (gameMode !== 'klassiekers' && window.matchMedia('(hover: hover)').matches) {
                                const note = notes?.[`desc_${race}`];
                                if (note) {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  
                                  // Clear existing timeout
                                  if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                                  
                                  hoverTimeoutRef.current = setTimeout(() => {
                                    setHoveredHeader(race);
                                    setHeaderTooltipPos({
                                      top: rect.top - 5,
                                      left: rect.left + rect.width / 2
                                    });
                                    setShowHeaderTooltip(true);
                                  }, 150);
                                }
                              }
                            }}
                            onMouseLeave={() => {
                              if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                              setShowHeaderTooltip(false);
                              setHoveredHeader(null);
                            }}
                          >
                            {getAbbr(race)}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const riderMap = Object.fromEntries(allRiders.map(a => [a.rider, a]));

                      // Use maxCoverageRows from parent for consistent table heights across configs
                      const multipliers = ['3', '2.5', '2'];
                      return Array.from({ length: maxCoverageRows }, (_, idx) => (
                        <tr key={idx} className={`cov-multiplier-row${idx === 2 ? ' cov-x2-border' : ''}`}>
                          <td className="cov-multiplier-cell">{multipliers[idx] || (idx >= 3 ? '-' : '')}</td>
                          {races.map(race => {
                            const effectiveTeam = getEffectiveTeamForRace(race);
                            const gerankte = (favoriteRiders[race] || [])
                              .filter(f => effectiveTeam.some(sr => isSame(sr, f.rider)))
                              .sort((a, b) => a.rank - b.rank)
                              .map(f => f.rider);
                            const extra = effectiveTeam
                              .filter(sr => (riderMap[sr]?.races || []).includes(race) && !gerankte.some(gr => isSame(gr, sr)))
                              .sort((a, b) => a.localeCompare(b));
                            const allRiders = [...gerankte, ...extra];
                            const rider = allRiders[idx];
                            const rawFirstName = rider ? rider.split(' ')[0] : '—';
                            const firstName = truncateToOneDot(rawFirstName);
                            // Check of deze renner niet als potentiele kopman is ingevuld bij deze wedstrijd
                            const isRed = rider && !((favoriteRiders[race] || []).some(f => isSame(f.rider, rider)));

                            // Check for swap indicators
                            const swapOutRace = rider ? getSwapOutRace(rider) : null;
                            const swapInRace = rider ? getSwapInRace(rider) : null;
                            const isSwapOutCell = swapOutRace === race;
                            const isSwapInCell = swapInRace === race;

                            // Tooltip: available favorites not yet selected
                            const cellId = `${race}-${idx}`;
                            const isHovered = hoveredCell === cellId;
                            const isEmpty = !rider;
                            const availableFavorites = isEmpty
                              ? (favoriteRiders[race] || [])
                                .filter(f => !effectiveTeam.includes(f.rider))
                                .sort((a, b) => a.rank - b.rank)
                              : [];

                            const favEntry = (favoriteRiders[race] || []).find(f => isSame(f.rider, rider));
                            // In the coverage table, if a rider is shown here, they are by definition selected for this race.
                            const color = (favEntry && rider) ? getRankColor(favEntry.rank, race) : null;

                            return (
                              <td
                                key={race}
                                className={`cov-cell${isRed && rider ? ' cov-cell-red' : ''}${isHovered ? ' cov-cell-hover' : ''}${isSwapOutCell ? ' cov-cell-swap-out' : ''}${isSwapInCell ? ' cov-cell-swap-in' : ''}`}
                                style={{
                                  cursor: rider ? 'pointer' : 'default',
                                  backgroundColor: color || undefined,
                                  color: color ? 'white' : undefined,
                                  borderColor: color ? 'rgba(255,255,255,0.2)' : undefined
                                }}
                                title={rider ? "Bekijk historische uitslagen" : undefined}
                                onClick={() => {
                                  if (rider) {
                                    const riderData = riderMap[rider];
                                    onShowHistory({ 
                                      name: rider, 
                                      riderId: riderData?.riderId || null,
                                      team_abbr: riderData?.team_abbr || null,
                                      klassement: riderData?.klassement || 0,
                                      klimmen: riderData?.klimmen || 0,
                                      tijdrijden: riderData?.tijdrijden || 0,
                                      sprinten: riderData?.sprinten || 0,
                                      punchen: riderData?.punchen || 0,
                                      heuvels: riderData?.heuvels || 0,
                                      kasseien: riderData?.kasseien || 0
                                    });
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  setHoveredCell(cellId);
                                  if (isEmpty) {
                                    // Calculate position immediately
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const pos = {
                                      top: rect.bottom + 5,
                                      left: rect.left + rect.width / 2
                                    };
                                    // Clear any existing timeout
                                    if (hoverTimeoutRef.current) {
                                      clearTimeout(hoverTimeoutRef.current);
                                    }
                                    // Set timeout for 200ms before showing tooltip content
                                    hoverTimeoutRef.current = setTimeout(() => {
                                      setTooltipPos(pos);
                                      setShowTooltip(true);
                                    }, 200);
                                  }
                                }}
                                onMouseLeave={() => {
                                  // Reset everything immediately
                                  if (hoverTimeoutRef.current) {
                                    clearTimeout(hoverTimeoutRef.current);
                                  }
                                  setHoveredCell(null);
                                  setShowTooltip(false);
                                }}
                              >
                                {firstName}
                                {isEmpty && showTooltip && isHovered && availableFavorites.length > 0 && (
                                  <div
                                    className="cov-cell-tooltip"
                                    style={{
                                      top: `${tooltipPos.top}px`,
                                      left: `${tooltipPos.left}px`,
                                      transform: 'translateX(-50%)'
                                    }}
                                  >
                                    {availableFavorites.map(fav => (
                                      <div key={fav.rider} className="tooltip-rider">
                                        {fav.rider.split(' ')[0]}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Swap Summary */}
              <div className="swap-summary">
                <div className="swap-content-left">
                  <div className="swap-label">Wissels:</div>
                  <div className="swap-items-container">
                    {appliedSwaps.map((swap, idx) => {
                      if (!swap.out || !swap.in || !appliedSelectedSwapAfterRace[idx]) return null;
                      const outLastName = swap.out.split(' ')[0];
                      const inLastName = swap.in.split(' ')[0];
                      return (
                         <div key={idx} className="swap-item">
                          <span className="swap-out">{outLastName}</span> → <span className="swap-in">{inLastName}</span> na <span className="swap-race-full">{appliedSelectedSwapAfterRace[idx]}</span><span className="swap-race-abbr">{getAbbr(appliedSelectedSwapAfterRace[idx])}</span>
                          <button
                            className="swap-summary-delete-button"
                            onClick={() => removeAppliedSwapWithValidation(idx)}
                            title="Wissel verwijderen"
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {gameMode === 'klassiekers' && (
                  <div className="participation-counter">
                    Aantal deelnames: <span className="participation-count">{totalParticipations.toString().padStart(3, '\u00A0')}</span>
                  </div>
                )}
              </div>
            </>
          )}

          {(gameMode !== 'klassiekers' || tableView === 'favorites') && (
            <>
              <div className="favorites-table">
                <table>
                  <thead>
                    <tr>
                      <th className="fav-rank-header"></th>
                      {races.map(race => {
                        const comp = competitions.find(c => c.TranslatedName === race);
                        return (
                          <th
                            key={race}
                            className={`fav-race-header clickable-header`}
                            onClick={gameMode === 'klassiekers' ? () => onShowTop20(race, comp?.CompetitionId) : () => onOpenNotes(race)}
                            title={gameMode === 'klassiekers' ? "Bekijk top 20 uitslagen" : undefined}
                            onMouseEnter={(e) => {
                              if (gameMode !== 'klassiekers' && window.matchMedia('(hover: hover)').matches) {
                                const note = notes?.[`desc_${race}`];
                                if (note) {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  
                                  // Clear existing timeout
                                  if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                                  
                                  hoverTimeoutRef.current = setTimeout(() => {
                                    setHoveredHeader(race);
                                    setHeaderTooltipPos({
                                      top: rect.top - 5,
                                      left: rect.left + rect.width / 2
                                    });
                                    setShowHeaderTooltip(true);
                                  }, 150);
                                }
                              }
                            }}
                            onMouseLeave={() => {
                              if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                              setShowHeaderTooltip(false);
                              setHoveredHeader(null);
                            }}
                          >
                            {getAbbr(race)}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Use maxFavoritesRankPerRace from parent for consistent table heights across configs
                      const maxRank = Math.max(3, maxFavoritesRankPerRace);

                      return Array.from({ length: maxRank }, (_, idx) => (
                        <tr key={idx} className="fav-rank-row">
                          <td className="fav-rank-cell">{idx + 1}</td>
                          {races.map(race => {
                            const effectiveTeam = getEffectiveTeamForRace(race);
                            const favList = (favoriteRiders[race] || []).sort((a, b) => a.rank - b.rank);
                            const fav = favList[idx];
                            let rider = fav?.rider || '';
                            if (gameMode !== 'klassiekers' && invalidRiderNames.includes(rider)) {
                                rider = '';
                            }
                            const isSelected = effectiveTeam.some(sr => isSame(sr, rider));
                            const rawLastName = rider ? getLastName(rider) : '';
                            const lastName = truncateToOneDot(rawLastName);

                            const cellId = `fav-${race}-${idx}`;
                            const isHovered = hoveredCell === cellId;
                            // Only show color if the rider is selected
                            const color = (rider && isSelected) ? getRankColor(fav.rank, race) : null;

                            return (
                              <td
                                key={race}
                                className={`fav-cell${rider && isSelected ? ' fav-cell-selected' : ''}${rider && isHovered ? ' cov-cell-hover' : ''}`}
                                style={{
                                  cursor: rider ? 'pointer' : 'default',
                                  backgroundColor: color || undefined,
                                  color: color ? 'white' : undefined,
                                  borderColor: color ? 'rgba(255,255,255,0.2)' : undefined
                                }}
                                title={rider ? "Bekijk historische uitslagen" : undefined}
                                onClick={() => {
                                  if (rider) {
                                    const rMap = Object.fromEntries(allRiders.map(a => [a.rider, a]));
                                    const riderData = rMap[rider];
                                    onShowHistory({ 
                                      name: rider, 
                                      riderId: riderData?.riderId || null,
                                      team_abbr: riderData?.team_abbr || null,
                                      klassement: riderData?.klassement || 0,
                                      klimmen: riderData?.klimmen || 0,
                                      tijdrijden: riderData?.tijdrijden || 0,
                                      sprinten: riderData?.sprinten || 0,
                                      punchen: riderData?.punchen || 0,
                                      heuvels: riderData?.heuvels || 0,
                                      kasseien: riderData?.kasseien || 0
                                    });
                                  }
                                }}
                                onMouseEnter={() => {
                                  if (rider) setHoveredCell(cellId);
                                }}
                                onMouseLeave={() => {
                                  setHoveredCell(null);
                                }}
                              >
                                {lastName}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Swap Summary - Hidden for Giro */}
              {gameMode === 'klassiekers' && (
                <div className="swap-summary">
                  <div className="swap-content-left">
                    <div className="swap-label">Wissels:</div>
                    <div className="swap-items-container">
                      {appliedSwaps.map((swap, idx) => {
                        if (!swap.out || !swap.in || !appliedSelectedSwapAfterRace[idx]) return null;
                        const outLastName = swap.out.split(' ')[0];
                        const inLastName = swap.in.split(' ')[0];
                        return (
                           <div key={idx} className="swap-item">
                            <span className="swap-out">{outLastName}</span> → <span className="swap-in">{inLastName}</span> na <span className="swap-race-full">{appliedSelectedSwapAfterRace[idx]}</span><span className="swap-race-abbr">{getAbbr(appliedSelectedSwapAfterRace[idx])}</span>
                            <button
                              className="swap-summary-delete-button"
                              onClick={() => removeAppliedSwapWithValidation(idx)}
                              title="Wissel verwijderen"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {gameMode === 'klassiekers' && (
                    <div className="participation-counter">
                      Aantal deelnames: <span className="participation-count">{totalParticipations.toString().padStart(3, '\u00A0')}</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {showPlannedSwaps && (
        <div className="swaps-modal-overlay" onClick={() => {
          // Bij sluiten via overlay: altijd parent state herstellen
          setWorkingPlannedSwaps(deepCopySwaps(plannedSwaps));
          setWorkingSelectedSwapAfterRace(selectedSwapAfterRace);
          setInputValues({
            '0-out': '', '0-in': '',
            '1-out': '', '1-in': '',
            '2-out': '', '2-in': ''
          });

          setOpenDropdown(null);
          setShowPlannedSwaps(false);
        }}>
          <div className="swaps-modal" onClick={(e) => e.stopPropagation()}>
            <div className="swaps-modal-header">
              <h3>Geplande wissels</h3>
              <button className="swaps-close-button" onClick={() => {
                // Bij sluiten via close-button: altijd parent state herstellen
                setWorkingPlannedSwaps(deepCopySwaps(plannedSwaps));
                setWorkingSelectedSwapAfterRace(selectedSwapAfterRace);
                setInputValues({
                  '0-out': '', '0-in': '',
                  '1-out': '', '1-in': '',
                  '2-out': '', '2-in': ''
                });

                setOpenDropdown(null);
                setShowPlannedSwaps(false);
              }}>
                ✕
              </button>
            </div>
            <div className="swaps-modal-content">
              {workingPlannedSwaps.map((swap, index) => {
                return (
                  <div key={index} className="swap-row">
                    <button
                      className={`swap-delete-button ${!swap.out ? 'hidden' : ''}`}
                      onClick={() => removeSwapWithValidation(index)}
                      title="Wissel verwijderen"
                      disabled={!swap.out}
                    >
                      ✕
                    </button>
                    <div className="swap-input-container">
                      <input
                        type="text"
                        placeholder="Uit"
                        value={swap.out ? `${swap.out} (€ ${(getRiderPrice(swap.out) / 1000000).toFixed(2)}M)` : ''}
                        readOnly
                        onClick={() => {
                          // Allow clicking only if it's the first row or the previous row has a rider selected
                          if (index === 0 || workingPlannedSwaps[index - 1].out) {
                            setOpenDropdown(`${index}-out`);
                          }
                        }}
                        className="swap-input"
                        disabled={index > 0 && !workingPlannedSwaps[index - 1].out}
                      />
                      {openDropdown === `${index}-out` && (
                        <div className="swap-dropdown">
                          <div
                            className="swap-dropdown-item"
                            onClick={() => {
                              removeSwapWithValidation(index);
                            }}
                          >
                            <span className="rider-name">-</span>
                          </div>
                          {getAvailableRiders(index, 'out').map(rider => (
                            <div
                              key={rider}
                              className="swap-dropdown-item"
                              onClick={() => {
                                const oldOutRider = workingPlannedSwaps[index].out;

                                // When OUT rider changes, ALWAYS clear the race and IN rider
                                const newSwaps = updateSwapAtIndex(workingPlannedSwaps, index, { out: rider, in: '' });
                                const newRaces = { ...workingSelectedSwapAfterRace, [index]: null };
                                updateWorkingSwaps(newSwaps, newRaces);
                                setInputValues({ ...inputValues, [`${index}-out`]: rider, [`${index}-in`]: '' });
                                setOpenDropdown(null);

                                // Cleanup: If old OUT rider was used as IN later, remove those INs
                                // (unless the old OUT rider is in selectedRiders)
                                const selectedSet = new Set(selectedRiders || []);
                                if (oldOutRider && !selectedSet.has(oldOutRider)) {
                                  const cleanedSwaps = newSwaps.map((swap, swapIdx) => {
                                    if (swapIdx > index && swap.in === oldOutRider) {
                                      return { out: '', in: '' };
                                    }
                                    return swap;
                                  });
                                  updateWorkingSwaps(cleanedSwaps, newRaces);
                                  // Clear IN values too
                                  const newInputValues = { ...inputValues, [`${index}-out`]: rider, [`${index}-in`]: '' };
                                  cleanedSwaps.forEach((swap, swapIdx) => {
                                    if (swapIdx > index && swap.in === '') {
                                      newInputValues[`${swapIdx}-in`] = '';
                                    }
                                  });
                                  setInputValues(newInputValues);
                                }

                                // Always open race selection modal when OUT rider is selected
                                setTimeout(() => {
                                  setSelectedRaceModalIndex(index);
                                  setShowRaceSelectionModal(true);
                                }, 100);
                              }}
                            >
                              <span className="rider-name">{rider}</span>
                              <span className="rider-price">€ {getRiderPrice(rider) ? (getRiderPrice(rider) / 1000000).toFixed(2) : '?'}M</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="swap-arrow">→</span>
                    <div className="swap-in-wrapper">
                      <div className="swap-input-container">
                        <input
                          type="text"
                          placeholder="In"
                          value={swap.in ? `${swap.in} (€ ${(getRiderPrice(swap.in) / 1000000).toFixed(2)}M)` : ''}
                          readOnly
                          onClick={() => {
                            if (workingSelectedSwapAfterRace[index]) {
                              setSelectedInModalIndex(index);
                              setShowInRiderModal(true);
                            }
                          }}
                          className="swap-input"
                          disabled={!workingSelectedSwapAfterRace[index]}
                        />
                      </div>
                      {workingSelectedSwapAfterRace[index] && (
                        <span
                          className="race-label"
                          onClick={() => {
                            setSelectedRaceModalIndex(index);
                            setShowRaceSelectionModal(true);
                          }}
                        >
                          Na {workingSelectedSwapAfterRace[index]}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="swaps-modal-footer">
              <div className="swap-costs-display">
                Wisselkosten: € {(calculateSwapDemand(workingPlannedSwaps, workingSelectedSwapAfterRace) / 1000000).toFixed(2)}M
                <span className="swap-costs-explanation"> (dit wordt bij je gespendeerde budget opgeteld)</span>
              </div>
              <button className="swaps-apply-button"
                onClick={() => {
                  // Copy the current workingPlannedSwaps to appliedSwaps via commitSwaps
                  commitSwaps(workingPlannedSwaps, workingSelectedSwapAfterRace, workingPlannedSwaps, workingSelectedSwapAfterRace);
                  setShowPlannedSwaps(false);
                }}
                disabled={!checkSwapsValid(workingPlannedSwaps, workingSelectedSwapAfterRace)}
              >
                Toepassen
              </button>
            </div>
          </div>

          {showRaceSelectionModal && selectedRaceModalIndex !== null && (
            <div className="race-selection-overlay" onClick={(e) => e.stopPropagation()}>
              <div className="race-selection-modal" onClick={(e) => e.stopPropagation()}>
                <div className="race-modal-header">
                  <div>
                    <h3>Na welke race moet deze wissel plaatsvinden?</h3>
                    <div className="race-legend">
                      <div className="legend-item">
                        <span className="legend-dot not-racing"></span>
                        <span>= doet niet mee</span>
                      </div>
                      <div className="legend-item">
                        <span className="legend-dot racing"></span>
                        <span>= doet mee</span>
                      </div>
                    </div>
                  </div>
                  <button className="race-modal-back-button" onClick={() => {
                    const index = selectedRaceModalIndex;
                    // If no race was selected, reset the swap
                    if (!workingSelectedSwapAfterRace[index]) {
                      const selectedSet = new Set(selectedRiders || []);
                      const oldOutRider = workingPlannedSwaps[index].out;
                      const newSwaps = updateSwapAtIndex(workingPlannedSwaps, index, { out: '', in: '' });
                      setInputValues({ ...inputValues, [`${index}-out`]: '', [`${index}-in`]: '' });

                      // Cleanup: If old OUT rider was used as IN later, remove those INs
                      if (oldOutRider && !selectedSet.has(oldOutRider)) {
                        const cleanedSwaps = newSwaps.map((swap, swapIdx) => {
                          if (swapIdx > index && swap.in === oldOutRider) {
                            return { out: '', in: '' };
                          }
                          return swap;
                        });
                        updateWorkingSwaps(cleanedSwaps, workingSelectedSwapAfterRace);
                      } else {
                        updateWorkingSwaps(newSwaps, workingSelectedSwapAfterRace);
                      }
                    }
                    setShowRaceSelectionModal(false);
                  }}>
                    ← Terug
                  </button>
                </div>
                <div className="race-grid">
                  {races.map((race) => {
                    const index = selectedRaceModalIndex;
                    const outRider = workingPlannedSwaps[index]?.out;
                    const riderRaces = outRider
                      ? (allRiders.find(r => r.rider === outRider)?.races || [])
                      : [];
                    const isRacing = riderRaces.includes(race);

                    // Check OUT rider constraints - determine if OUT rider is in team at this race
                    let isDisabled = false;

                    if (outRider) {
                      const isOriginallySelected = selectedRiders.includes(outRider);
                      const currentRaceIndex = races.indexOf(race);

                      // Track the last event (IN or OUT) for this rider BEFORE the current swap index
                      let lastEventType = null; // 'in' or 'out'
                      let lastEventRaceIndex = -1;

                      for (let i = 0; i < index; i++) {
                        const swapRace = workingSelectedSwapAfterRace[i];
                        if (!swapRace) continue;
                        const swapRaceIndex = races.indexOf(swapRace);

                        if (workingPlannedSwaps[i].out === outRider) {
                          if (swapRaceIndex > lastEventRaceIndex) {
                            lastEventRaceIndex = swapRaceIndex;
                            lastEventType = 'out';
                          }
                        }
                        if (workingPlannedSwaps[i].in === outRider) {
                          if (swapRaceIndex > lastEventRaceIndex) {
                            lastEventRaceIndex = swapRaceIndex;
                            lastEventType = 'in';
                          }
                        }
                      }

                      // Determine if OUT rider is currently "in team" or "out of team"
                      // A rider can only be OUT'd if they're IN the team at that point
                      if (isOriginallySelected) {
                        // Originally selected: in team unless last event was OUT
                        if (lastEventType === 'out') {
                          // Rider was OUT'd → can only OUT again after being IN'd again
                          // But there's no IN after the OUT, so cannot OUT at any race
                          // Actually, we need to check if there's an IN AFTER the OUT
                          // No, if last event is OUT, they're OUT of team and cannot be OUT'd again
                          isDisabled = true;
                        } else if (lastEventType === 'in') {
                          // Rider was IN'd (after being OUT'd earlier) → can OUT after the IN race
                          if (currentRaceIndex <= lastEventRaceIndex) {
                            isDisabled = true;
                          }
                        }
                        // If lastEventType is null, originally selected and in team → available for OUT
                      } else {
                        // Not originally selected: NOT in team unless last event was IN
                        if (lastEventType === null || lastEventType === 'out') {
                          // Rider is not in team → cannot OUT them
                          isDisabled = true;
                        } else if (lastEventType === 'in') {
                          // Rider was IN'd → can OUT after the IN race
                          if (currentRaceIndex <= lastEventRaceIndex) {
                            isDisabled = true;
                          }
                        }
                      }

                      // Also check LATER swaps: if OUT rider is used as IN later
                      if (!isDisabled) {
                        let earliestLaterInRaceIndex = -1;
                        for (let i = index + 1; i < workingPlannedSwaps.length; i++) {
                          if (workingPlannedSwaps[i].in === outRider && workingSelectedSwapAfterRace[i]) {
                            const inRace = workingSelectedSwapAfterRace[i];
                            const inRaceIndex = races.indexOf(inRace);
                            if (earliestLaterInRaceIndex === -1 || inRaceIndex < earliestLaterInRaceIndex) {
                              earliestLaterInRaceIndex = inRaceIndex;
                            }
                          }
                        }

                        // Current OUT race must be BEFORE the earliest later IN race
                        if (earliestLaterInRaceIndex !== -1) {
                          if (currentRaceIndex >= earliestLaterInRaceIndex) {
                            isDisabled = true;
                          }
                        }
                      }
                    }

                    // Check IN rider constraints - determine if IN rider is available at this race
                    const inRider = workingPlannedSwaps[index]?.in;
                    if (inRider && !isDisabled) {
                      const isOriginallySelected = selectedRiders.includes(inRider);
                      const currentRaceIndex = races.indexOf(race);

                      // Track the last event (IN or OUT) for this rider BEFORE the current swap index
                      let lastEventType = null; // 'in' or 'out'
                      let lastEventRaceIndex = -1;

                      for (let i = 0; i < index; i++) {
                        const swapRace = workingSelectedSwapAfterRace[i];
                        if (!swapRace) continue;
                        const swapRaceIndex = races.indexOf(swapRace);

                        if (workingPlannedSwaps[i].out === inRider) {
                          if (swapRaceIndex > lastEventRaceIndex) {
                            lastEventRaceIndex = swapRaceIndex;
                            lastEventType = 'out';
                          }
                        }
                        if (workingPlannedSwaps[i].in === inRider) {
                          if (swapRaceIndex > lastEventRaceIndex) {
                            lastEventRaceIndex = swapRaceIndex;
                            lastEventType = 'in';
                          }
                        }
                      }

                      // Determine if IN rider is currently "in team" or "out of team"
                      // A rider can only be IN'd if they're NOT in the team at that point
                      if (isOriginallySelected) {
                        // Originally selected: in team unless last event was OUT
                        if (lastEventType === null || lastEventType === 'in') {
                          // Rider is still in team → cannot IN them at any race
                          isDisabled = true;
                        } else if (lastEventType === 'out') {
                          // Rider was OUT'd → can only IN after the OUT race
                          if (currentRaceIndex <= lastEventRaceIndex) {
                            isDisabled = true;
                          }
                        }
                      } else {
                        // Not originally selected: NOT in team unless last event was IN
                        if (lastEventType === 'in') {
                          // Rider was IN'd and is now in team → cannot IN them again
                          isDisabled = true;
                        } else if (lastEventType === 'out') {
                          // Rider was OUT'd after being IN'd → can IN after the OUT race
                          if (currentRaceIndex <= lastEventRaceIndex) {
                            isDisabled = true;
                          }
                        }
                        // If lastEventType is null, rider is available (not in team, never swapped)
                      }

                      // Also check LATER swaps: if IN rider is used as OUT anywhere after
                      if (!isDisabled) {
                        let earliestLaterOutRaceIndex = -1;
                        for (let i = index + 1; i < workingPlannedSwaps.length; i++) {
                          if (workingPlannedSwaps[i].out === inRider && workingSelectedSwapAfterRace[i]) {
                            const outRace = workingSelectedSwapAfterRace[i];
                            const outRaceIndex = races.indexOf(outRace);
                            if (earliestLaterOutRaceIndex === -1 || outRaceIndex < earliestLaterOutRaceIndex) {
                              earliestLaterOutRaceIndex = outRaceIndex;
                            }
                          }
                        }

                        // Current IN race must be BEFORE the earliest later OUT race
                        if (earliestLaterOutRaceIndex !== -1) {
                          if (currentRaceIndex >= earliestLaterOutRaceIndex) {
                            isDisabled = true;
                          }
                        }
                      }
                    }

                    return (
                      <div
                        key={race}
                        className={`race-item ${isRacing ? 'racing' : 'not-racing'} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => {
                          if (!isDisabled) {
                            const inRider = workingPlannedSwaps[index].in;

                            // Check if "In" rider is still valid with this new race
                            if (!isInRiderValid(index, inRider, race)) {
                              const newSwaps = [...workingPlannedSwaps];
                              newSwaps[index].in = '';
                              updateWorkingSwaps(newSwaps, workingSelectedSwapAfterRace);
                              setInputValues({ ...inputValues, [`${index}-in`]: '' });
                            }

                            updateWorkingSwaps(workingPlannedSwaps, {
                              ...workingSelectedSwapAfterRace,
                              [index]: race
                            });
                            setShowRaceSelectionModal(false);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        {race}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showSwapWarning && (
        <div className="swap-warning-overlay" onClick={() => setShowSwapWarning(false)}>
          <div className="swap-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="swap-warning-content">
              <h3>⚠️ Waarschuwing</h3>
              {swapWarningData.warningType === 'in-rider-change' ? (
                <>
                  <p>
                    Als je <strong>{swapWarningData.riderName}</strong> verandert, zal de volgende wissel worden verwijderd omdat deze renner daar later als uit-renner staat ingesteld:
                  </p>
                  <ul className="affected-swaps-list">
                    {swapWarningData.affectedSwaps.map(idx => {
                      const swapSource = workingPlannedSwaps;
                      return (
                        <li key={idx}>
                          Rij {idx + 1}: {getLastName(swapSource[idx].out)} → {getLastName(swapSource[idx].in)}
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : (
                <>
                  {swapWarningData.pendingRemove.type === 'applied' ? (
                    <>
                      {swapWarningData.dependencyType && (
                        <>
                          <p>
                            Waarschuwing: <strong>{swapWarningData.riderName}</strong> wordt gebruikt in deze latere wissel(s):
                          </p>
                          <ul className="affected-swaps-list">
                            {swapWarningData.affectedSwaps
                              .filter(idx => !swapWarningData.budgetIssues?.includes(idx))
                              .map(idx => {
                                const swapSource = appliedSwaps;
                                return (
                                  <li key={idx}>
                                    Rij {idx + 1}: {getLastName(swapSource[idx].out)} → {getLastName(swapSource[idx].in)}
                                  </li>
                                );
                              })}
                          </ul>
                        </>
                      )}
                      <p>Als je doorgaat zullen ook deze wissel(s) worden verwijderd.</p>
                    </>
                  ) : (
                    <>
                      <p>
                        Als je deze wissel verwijdert worden alle latere wissels waar <strong>{swapWarningData.riderName}</strong> in voorkomt ook verwijderd. Andere wissels:
                      </p>
                      <ul className="affected-swaps-list">
                        {swapWarningData.affectedSwaps.map(idx => {
                          const swapSource = workingPlannedSwaps;
                          return (
                            <li key={idx}>
                              Rij {idx + 1}: {getLastName(swapSource[idx].out)} → {getLastName(swapSource[idx].in)}
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                </>
              )}
              <div className="swap-warning-buttons">
                <button className="warning-cancel-btn" onClick={() => setShowSwapWarning(false)}>
                  Annuleren
                </button>
                <button className="warning-confirm-btn" onClick={() => {
                  if (swapWarningData.warningType === 'in-rider-change') {
                    handleInRiderConflictResolution();
                  } else if (swapWarningData.pendingRemove.type === 'applied') {
                    // For applied swaps: remove the selected swap AND all dependent swaps
                    performAppliedSwapRemoval(swapWarningData.indexToRemove, swapWarningData.affectedSwaps);
                  } else {
                    performSwapRemoval(swapWarningData.indexToRemove, swapWarningData.affectedSwaps);
                  }
                }}>
                  Doorgaan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeselectWarning && (
        <div className="swap-warning-overlay" onClick={() => setShowDeselectWarning(false)}>
          <div className="swap-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="swap-warning-content">
              <h3>⚠️ Waarschuwing</h3>
              <p>
                Als je <strong>{deselectWarningData.riderName}</strong> deselecteert, zullen de volgende wissel(s) worden verwijderd:
              </p>
              <ul className="affected-swaps-list">
                {deselectWarningData.swapIndices.map(idx => {
                  const isBudgetIssue = deselectWarningData.budgetIssues?.includes(idx);
                  return (
                    <li key={idx}>
                      Rij {idx + 1}: {getLastName(workingPlannedSwaps[idx]?.out)} → {getLastName(workingPlannedSwaps[idx]?.in)}
                      {isBudgetIssue && <span style={{ color: '#ff6b6b' }}> (budgettechnisch onmogelijk)</span>}
                    </li>
                  );
                })}
              </ul>
              <div className="swap-warning-buttons">
                <button className="warning-cancel-btn" onClick={() => setShowDeselectWarning(false)}>
                  Annuleren
                </button>
                <button className="warning-confirm-btn" onClick={() => {
                  performDeselectWithSwapRemoval(deselectWarningData.pendingRider, deselectWarningData.swapIndices);
                }}>
                  Doorgaan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInRiderModal && selectedInModalIndex !== null && (
        <div className="in-rider-selector-overlay" onClick={() => {
          setShowInRiderModal(false);
          setShowInModalRaceFilter(false);
        }}>
          <div className="in-rider-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="in-rider-selector-header">
              <h3>Selecteer inkomende renner</h3>
              <button className="swaps-close-button" onClick={() => {
                setShowInRiderModal(false);
                setShowInModalRaceFilter(false);
              }}>
                ✕
              </button>
            </div>

            <div className="in-rider-selector-controls">
              <label className="in-modal-checkbox">
                <input
                  type="checkbox"
                  checked={inModalFilterFavs}
                  onChange={(e) => setInModalFilterFavs(e.target.checked)}
                />
                Alleen favorieten
              </label>

              <select
                value={inModalSortBy}
                onChange={(e) => setInModalSortBy(e.target.value)}
                className="in-modal-sort"
              >
                <option value="price">Sorteer op prijs</option>
                <option value="race_count">
                  {gameMode === 'klassiekers' ? 'Sorteer op aantal wedstrijden' : 'Sorteer op aantal favoriet'}
                </option>
                <option value="name">Sorteer op naam</option>
              </select>

              <div className="in-race-filter-container" ref={inModalRaceFilterRef}>
                <button
                  className="in-race-filter-button"
                  onClick={() => setShowInModalRaceFilter(!showInModalRaceFilter)}
                >
                  {gameMode !== 'klassiekers' ? 'Etappe' : 'Wedstrijd'} filter {inModalSelectedRaces.length > 0 && `(${inModalSelectedRaces.length})`}
                </button>
                {showInModalRaceFilter && (
                  <div className="in-race-filter-dropdown" onClick={(e) => e.stopPropagation()}>
                    <div className="in-race-filter-header">
                      <span>Kies {gameMode !== 'klassiekers' ? 'etappes' : 'wedstrijden'}:</span>
                      {inModalSelectedRaces.length > 0 && (
                        <button
                          className="in-clear-races-button"
                          onClick={() => setInModalSelectedRaces([])}
                        >
                          Wissen
                        </button>
                      )}
                    </div>
                    <div className="in-race-filter-list">
                      {races.map(race => (
                        <label key={race} className="in-race-filter-item">
                          <input
                            type="checkbox"
                            checked={inModalSelectedRaces.includes(race)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setInModalSelectedRaces([...inModalSelectedRaces, race]);
                              } else {
                                setInModalSelectedRaces(inModalSelectedRaces.filter(r => r !== race));
                              }
                            }}
                          />
                          <span>{getAbbr(race)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="in-rider-selector-grid">
              {getAvailableRidersForInModal(selectedInModalIndex).map(rider => {
                const isFav = Object.values(favoriteRiders || {}).some(list =>
                  (list || []).some(f => f.rider === rider)
                );
                const favoriteRaces = Object.entries(favoriteRiders || {})
                  .filter(([race, list]) => (list || []).some(f => f.rider === rider))
                  .map(([race]) => race);
                const riderData = allRiders.find(r => r.rider === rider);
                const price = riderData?.price || 0;

                return (
                  <div
                    key={rider}
                    className={`in-puzzle-rider ${isFav ? 'favorite' : ''}`}
                    onClick={() => {
                      // If clicking same rider as current, just close modal
                      if (rider === workingPlannedSwaps[selectedInModalIndex].in) {
                        setShowInRiderModal(false);
                        return;
                      }

                      // Check if current "in" rider (being changed) will be affected
                      const conflict = checkInRiderChange(selectedInModalIndex);
                      if (conflict.hasConflict) {
                        // Show warning that current "in" rider is used as "out" later
                        setSwapWarningData({
                          riderName: workingPlannedSwaps[selectedInModalIndex].in, // current "in" rider being changed
                          affectedSwaps: [conflict.conflictIndex],
                          indexToRemove: conflict.conflictIndex,
                          pendingRemove: { index: selectedInModalIndex, type: 'in-rider-change', newRider: rider },
                          dependencyType: 'in-used-as-out',
                          warningType: 'in-rider-change'
                        });
                        setShowSwapWarning(true);
                        return;
                      }

                      const newSwaps = updateSwapAtIndex(workingPlannedSwaps, selectedInModalIndex, { in: rider });
                      updateWorkingSwaps(newSwaps, workingSelectedSwapAfterRace);
                      setShowInRiderModal(false);
                    }}
                  >
                    <div className="rider-info">
                      <div className="rider-name">{rider}</div>
                      <div className="rider-price">€ {(price / 1000000).toFixed(2)}M</div>
                      <div className="favorite-races">
                        {races.filter(rv => riderData?.races.includes(rv)).map(rv => {
                          const isFavRace = favoriteRaces.includes(rv);
                          const abbr = getAbbr(rv);
                          return (
                            <span
                              key={rv}
                              className={`race-badge ${isFavRace ? 'fav' : 'not-fav'}`}
                            >
                              {abbr}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showNamingModal && (
        <div className="naming-modal-overlay" onClick={() => setShowNamingModal(false)}>
          <div className="naming-modal" onClick={(e) => e.stopPropagation()}>
            <div className="naming-modal-header">
              <h3>Configuratie naam</h3>
              <button className="swaps-close-button" onClick={() => setShowNamingModal(false)}>✕</button>
            </div>
            <div className="naming-modal-content">
              <p>Geef een naam aan configuratie {namingConfigId}:</p>
              <input
                type="text"
                className="naming-input"
                value={tempConfigName}
                onChange={(e) => setTempConfigName(e.target.value)}
                placeholder="Bijv. met Wout, met Mads..."
                maxLength={50}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tempConfigName.length <= 50) {
                    onUpdateConfigName(namingConfigId, tempConfigName);
                    setShowNamingModal(false);
                  }
                }}
              />
            </div>
            <div className="naming-modal-footer">
              <button className="warning-cancel-btn" onClick={() => setShowNamingModal(false)}>Annuleren</button>
              <button
                className="naming-save-btn"
                disabled={tempConfigName.length > 50}
                onClick={() => {
                  onUpdateConfigName(namingConfigId, tempConfigName);
                  setShowNamingModal(false);
                }}
              >
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}

      {showHeaderTooltip && hoveredHeader && notes?.[`desc_${hoveredHeader}`] && (
        <div
          className="header-note-tooltip"
          style={{
            position: 'fixed',
            top: `${headerTooltipPos.top}px`,
            left: `${headerTooltipPos.left}px`,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999
          }}
        >
          {notes[`desc_${hoveredHeader}`]}
        </div>
      )}
    </div>
  );
}

export default RiderPuzzle;
